// src/components/CalendarView/CalendarEventCard.tsx
import React, { useState, useRef, useCallback } from 'react';
import ActionMenuConversation from '@/components/ConversationItem/ActionMenuConversation';
import { convHasEditPermissions } from '@/lib/corgeeHelper';
import { dashboardService } from '@/services/dashboardService';
import { socketService } from '@/services/socketService';
import { conversationBus } from '@/lib/conversationBus';
import type { CalendarEvent, ActionMenuConfig } from './types';

interface CalendarEventCardProps {
  event: CalendarEvent;
  onOpenInfoWindow: (conversationId: number) => void;
  actionMenuConfig: ActionMenuConfig;
  onDuplicateItem?: (e: any, conv: any) => void;
  isLoading?: boolean;
}

function getLayerColor(event: CalendarEvent): string
{
  if (event.Color) return event.Color;
  const layers = event.Conversation?.ConversationLayers;
  if (layers && layers.length > 0) return layers[0]?.LayerColor || '#ccc';
  return '#ccc';
}

function isDueDate(fieldName: string): boolean
{
  return /due/i.test(fieldName);
}

export const CalendarEventCard: React.FC<CalendarEventCardProps> = ({
  event,
  onOpenInfoWindow,
  actionMenuConfig,
  onDuplicateItem,
  isLoading,
}) =>
{
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [localTitle, setLocalTitle] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const displayTitle = localTitle ?? (event.Conversation?.Title || `Task #${event.ConversationId}`);

  const handleClick = useCallback(() =>
  {
    if (clickTimerRef.current) return;
    clickTimerRef.current = setTimeout(() =>
    {
      clickTimerRef.current = null;
      onOpenInfoWindow(event.ConversationId);
    }, 220);
  }, [event.ConversationId, onOpenInfoWindow]);

  const handleDoubleClick = useCallback(
    (e: React.MouseEvent) =>
    {
      e.preventDefault();
      if (clickTimerRef.current)
      {
        clearTimeout(clickTimerRef.current);
        clickTimerRef.current = null;
      }
      const canEdit = convHasEditPermissions(event.Conversation as any);
      if (!canEdit)
      {
        onOpenInfoWindow(event.ConversationId);
        return;
      }
      setEditTitle(displayTitle);
      setIsEditing(true);
      setTimeout(() => inputRef.current?.select(), 0);
    },
    [event.Conversation, event.ConversationId, displayTitle, onOpenInfoWindow],
  );

  const saveTitle = useCallback(async () =>
  {
    const trimmed = editTitle.trim();
    if (!trimmed)
    {
      setIsEditing(false);
      return;
    }
    setIsEditing(false);
    setLocalTitle(trimmed);
    try
    {
      await dashboardService.updateconversationtitle(
        trimmed,
        event.Conversation?.ConfigValueId || 0,
        event.ConversationId,
        socketService.connectionId || '',
      );
      conversationBus.emit('title-updated', {
        conversationId: event.ConversationId,
        title: trimmed,
      });
    }
    catch (err)
    {
      console.error('CalendarEventCard: failed to update title', err);
      setLocalTitle(null);
    }
  }, [editTitle, event.ConversationId, event.Conversation]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) =>
    {
      if (e.key === 'Enter') saveTitle();
      else if (e.key === 'Escape') setIsEditing(false);
    },
    [saveTitle],
  );

  const isDark = document.documentElement.classList.contains('dark');

  if (isLoading)
  {
    return (
      <div className="cal-event-card" style={{ borderLeftColor: isDark ? '#4a4c4c' : '#ddd' }}>
        <div className="cal-skeleton cal-skeleton-title" />
        <div className="cal-skeleton cal-skeleton-badge" />
      </div>
    );
  }

  const color = getLayerColor(event);
  const isDue = isDueDate(event.CustomFieldName);
  const isRedFlag = event.Conversation?.IsRedFlag;

  return (
    <div className="cal-event-card" style={{ borderLeftColor: color }}>
      <div className="cal-event-card-top">
        {isEditing ? (
          <input
            ref={inputRef}
            className="cal-event-title-input"
            type="text"
            value={editTitle}
            autoFocus
            onChange={e => setEditTitle(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={saveTitle}
          />
        ) : (
          <span
            className="cal-event-title"
            title={displayTitle}
            onClick={handleClick}
            onDoubleClick={handleDoubleClick}
          >
            {displayTitle}
          </span>
        )}
        <ActionMenuConversation
          {...({
            conversation: event.Conversation,
            api: actionMenuConfig.api,
            config: actionMenuConfig.config,
            storageData: actionMenuConfig.storageData,
            configMenus: actionMenuConfig.configMenus,
            realItemTypes: actionMenuConfig.realItemTypes,
            userData: actionMenuConfig.userData,
            HasItemCreationPermission: localStorage.getItem('C2_hasItemCreationPermission') === 'true',
            corgeeFunctions: {
              openInfoWindow: (conv: any) => onOpenInfoWindow(conv.ConversationId),
              onDuplicateItem: onDuplicateItem ?? (() => {}),
            },
          } as any)}
        />
      </div>
      <div className="cal-event-bottom">
        <span className={isDue ? 'cal-badge-due' : 'cal-badge-start'}>{event.CustomFieldName}</span>
        {isRedFlag && (
          <span className="cal-redflag" title="Red Flag">
            &#9650;
          </span>
        )}
      </div>
    </div>
  );
};

export default CalendarEventCard;
